// SEO Configuration for MockTrail
// Comprehensive SEO and GEO (Generative Engine Optimization) settings

export interface PageSEO {
    title: string;
    description: string;
    keywords: string[];
    canonical?: string;
    ogImage?: string;
    ogType?: string;
    structuredData?: any[];
    alternates?: { [key: string]: string };
    noindex?: boolean;
    additionalMetaTags?: { name: string; content: string }[];
  }
  
  // Base SEO configuration shared across all pages
  export const baseSEO = {
    siteName: 'MockTrail',
    siteUrl: 'https://mocktrail.app',
    defaultImage: 'https://mocktrail.app/mocktrail-social-share.png',
    twitterHandle: '@mocktrail_ai',
    locale: 'en_US',
    themeColor: '#1a202c',
  };
  
  // Page-specific SEO configurations
  export const pageSEOData: { [key: string]: PageSEO } = {
    '/': {
      title: 'MockTrail - AI Interview Coach | Practice interviews with AI | For Students & Career Professionals',
      description: 'Master your interviews with MockTrail, the #1 AI interview coach. Practice realistic mock interviews, get instant AI feedback, and access personalized skill-based news. Perfect for students, graduates & job seekers. Start free AI interview practice today!',
      keywords: [
        'AI interview coach',
        'mock interview',
        'interview practice',
        'AI interview practice',
        'interview preparation',
        'job interview practice',
        'MockTrail',
        'online interview practice',
        'interview feedback',
        'career preparation',
        'AI mock interviews',
        'interview skills',
        'job interview tips',
        'interview coaching',
        'ai mock interviews',
        'virtual interview practice'
      ],
      structuredData: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "MockTrail AI Interview Coach",
          "alternateName": ["MockTrail", "Mock Trail", "MockTrail.app", "MockTrail AI"],
          "description": "AI-powered interview coaching platform that helps job seekers practice mock interviews and improve their interview skills with personalized feedback",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Any",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "0",
            "highPrice": "1999",
            "priceCurrency": "INR",
            "offerCount": "3"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Practice Interviews with MockTrail AI Coach",
          "description": "Step-by-step guide to using MockTrail for AI-powered interview practice",
          "step": [
            {
              "@type": "HowToStep",
              "name": "Sign Up",
              "text": "Create your free MockTrail account",
              "url": "https://platform.mocktrail.app/signup"
            },
            {
              "@type": "HowToStep",
              "name": "Choose Interview Type",
              "text": "Select technical, behavioral, or role-specific interview practice"
            },
            {
              "@type": "HowToStep",
              "name": "Practice with AI",
              "text": "Engage in realistic mock interviews with our AI interviewer"
            },
            {
              "@type": "HowToStep",
              "name": "Get Feedback",
              "text": "Receive detailed AI-powered feedback on your performance"
            }
          ]
        }
      ]
    },
    '/pricing': {
      title: 'MockTrail Pricing - Affordable AI Interview Coaching Plans | Start Free',
      description: 'Choose the perfect MockTrail plan for your interview preparation needs. Free plan available with basic AI interview practice. Premium plans starting at 1999/month with unlimited mock interviews and advanced AI feedback.',
      keywords: [
        'MockTrail pricing',
        'AI interview coach pricing',
        'mock interview cost',
        'interview practice pricing',
        'affordable interview coaching',
        'free interview practice',
        'interview preparation cost'
      ],
      structuredData: [
        {
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          "name": "MockTrail Pricing Plans",
          "price": "0-7499",
          "priceCurrency": "INR"
        }
      ]
    },
    '/blogs': {
      title: 'MockTrail Blog - Interview Tips, Career Advice & AI Interview Insights',
      description: 'Expert interview tips, career guidance, and AI interview preparation strategies. Learn how to ace your interviews with insights from MockTrail\'s AI interview coaches and industry experts.',
      keywords: [
        'interview tips',
        'career advice',
        'interview blog',
        'job search tips',
        'AI interview insights',
        'interview preparation guide',
        'career development'
      ],
      ogType: 'blog'
    },
    '/ambassador-program': {
      title: 'MockTrail Ambassador Program - Earn by Helping Others Practice Interviews',
      description: 'Join MockTrail Ambassador Program and earn money by referring students and job seekers to our AI interview coaching platform. Get exclusive benefits and help others succeed in their careers.',
      keywords: [
        'MockTrail ambassador',
        'referral program',
        'earn money online',
        'student ambassador program',
        'interview coach affiliate',
        'campus ambassador'
      ]
    },
    '/privacy-policy': {
      title: 'Privacy Policy - MockTrail AI Interview Coach',
      description: 'MockTrail privacy policy. Learn how we protect your data and ensure secure AI interview practice sessions.',
      keywords: ['MockTrail privacy', 'data protection', 'privacy policy'],
      noindex: false
    },
    '/terms-of-service': {
      title: 'Terms of Service - MockTrail AI Interview Platform',
      description: 'MockTrail terms of service. Understanding our platform guidelines for AI interview practice.',
      keywords: ['MockTrail terms', 'terms of service', 'platform guidelines'],
      noindex: false
    }
  };
  
  // Generate comprehensive meta tags for a page
  export function generateMetaTags(pathname: string): PageSEO {
    const pageData = pageSEOData[pathname] || pageSEOData['/'];
    const url = `${baseSEO.siteUrl}${pathname}`;
    
    return {
      ...pageData,
      canonical: url,
      ogImage: pageData.ogImage || baseSEO.defaultImage,
      ogType: pageData.ogType || 'website',
      additionalMetaTags: [
        // AI Search Engine Optimization Tags
        { name: 'ai-content-summary', content: pageData.description },
        { name: 'content-type', content: 'educational-platform' },
        { name: 'target-audience', content: 'job-seekers,students,professionals' },
        { name: 'service-category', content: 'ai-interview-coaching' },
        { name: 'platform-features', content: 'mock-interviews,ai-feedback,skill-news' },
        
        // Enhanced semantic tags for AI understanding
        { name: 'primary-service', content: 'AI-powered mock interview practice' },
        { name: 'secondary-service', content: 'Personalized skill-based career news' },
        { name: 'platform-name', content: 'MockTrail' },
        { name: 'platform-type', content: 'SaaS,EdTech,AI-Platform' },
        
        // Geographic and language targeting
        { name: 'geo.region', content: 'IN' },
        { name: 'geo.placename', content: 'India' },
        { name: 'language', content: 'en-US,en-IN,hi-IN' },
        
        // Additional SEO tags
        { name: 'revisit-after', content: '7 days' },
        { name: 'rating', content: 'general' },
        { name: 'distribution', content: 'global' },
        
        ...(pageData.additionalMetaTags || [])
      ]
    };
  }
  
  // FAQ data for structured data
  export const faqData = [
    {
      question: "What is MockTrail?",
      answer: "MockTrail is an AI-powered interview coaching platform that helps job seekers practice mock interviews with realistic AI interviewers. It provides instant feedback, personalized skill-based news, and comprehensive interview preparation tools."
    },
    {
      question: "How does MockTrail's AI interview coach work?",
      answer: "MockTrail uses advanced AI technology to simulate realistic interview scenarios. The AI interviewer asks relevant questions based on your role and experience, analyzes your responses in real-time, and provides detailed feedback on content, delivery, and communication skills."
    },
    {
      question: "Is MockTrail free to use?",
      answer: "Yes! MockTrail offers a free plan that includes basic AI interview practice features. For unlimited practice sessions, advanced feedback, and premium features, we offer paid plans starting at ₹1999/month."
    },
    {
      question: "Who can benefit from MockTrail?",
      answer: "MockTrail is perfect for college students, recent graduates, job seekers, career switchers, and professionals preparing for interviews. Our AI adapts to different experience levels and industries."
    },
    {
      question: "What types of interviews can I practice on MockTrail?",
      answer: "MockTrail offers practice for both technical & Non-technical interviews such as coding, system design rounds, behavioral interviews (STAR method), HR interviews, case interviews, and role-specific interviews for various industries including tech, consulting, finance, and marketing."
    },
    {
      question: "How is MockTrail different from other interview preparation platforms?",
      answer: "MockTrail combines AI-powered mock interviews with personalized skill-based news, making it a comprehensive career development platform. Our AI provides instant, detailed feedback and adapts to your specific needs, offering a more personalized experience than traditional prep methods."
    },
    {
      question: "Can I practice interviews in languages other than English?",
      answer: "Currently, MockTrail primarily supports English & Hindi for interview practice. We're actively working on adding support for more languages to help job seekers worldwide."
    },
    {
      question: "How accurate is MockTrail's AI feedback?",
      answer: "MockTrail's AI is trained on thousands of real interview scenarios and continuously improves through machine learning. Our feedback covers content quality, communication skills, body language (for video interviews), and provides actionable suggestions for improvement."
    },
    {
      question: "What is skill-based news on MockTrail?",
      answer: "Skill-based news is our AI-curated feed of industry insights, job market trends, and skill development resources personalized to your career interests and goals. It helps you stay updated with relevant information for your professional growth."
    },
    {
      question: "Can I track my interview performance over time?",
      answer: "Yes! MockTrail provides detailed analytics and progress tracking. You can see your improvement over time, identify strength and weakness patterns, and get personalized recommendations for areas to focus on."
    }
  ];
  
  // Generate FAQ structured data
  export function generateFAQSchema(faqs = faqData) {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }
  
  // Generate breadcrumb schema
  export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };
  }
  
  // Content optimization keywords for AI search engines
  export const aiSearchKeywords = {
    primary: [
      "AI interview coach",
      "mock interview practice",
      "interview preparation with AI",
      "MockTrail interview platform",
      "AI-powered interview feedback"
    ],
    secondary: [
      "online interview practice",
      "virtual mock interviews",
      "job interview simulator",
      "interview skills improvement",
      "career coaching AI"
    ],
    longtail: [
      "how to practice interviews with AI",
      "best AI interview coaching platform",
      "free mock interview practice online",
      "AI interview preparation for freshers",
      "technical interview practice with AI feedback"
    ],
    branded: [
      "MockTrail AI coach",
      "MockTrail interview practice",
      "MockTrail.app platform",
      "Mock Trail AI interviews"
    ]
  };
  
  // Rich snippets data
  export const richSnippetsData = {
    rating: {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2547",
      "bestRating": "5"
    },
    organization: {
      "@type": "Organization",
      "name": "MockTrail",
      "url": "https://mocktrail.app",
      "logo": "https://mocktrail.app/mocktrail-logo-bg.png",
      "sameAs": [
        "https://www.linkedin.com/company/mocktrail",
        "https://instagram.com/mocktrail.ai",
        "https://x.com/mocktrail_ai"
      ]
    }
  };